import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Download } from "lucide-react";
import DatePickerField from "../../components/common/DatePickerField";
import { listOffboardings, createOffboarding, exportExitReport, getClearanceProgress } from "./lifecycleApi";
import { getEmployees } from "../Employees/EmployeeApi";
import { downloadBlob } from "../Employees/employeeFileUtils";

const STATUSES = ["Draft", "Submitted", "Notice Period", "Clearance Process", "Final Settlement", "Exited", "Archived"];

const STATUS_COLORS = {
  Draft: "bg-slate-100 text-slate-700",
  Submitted: "bg-amber-100 text-amber-800",
  "Notice Period": "bg-blue-100 text-blue-800",
  "Clearance Process": "bg-orange-100 text-orange-800",
  "Final Settlement": "bg-purple-100 text-purple-800",
  Exited: "bg-red-100 text-red-800",
  Archived: "bg-slate-200 text-slate-600",
};

const SEPARATION_TYPES = [
  "Resignation", "Termination", "Retirement", "Contract Completion", "Absconding", "Mutual Separation",
];

export default function LcOffboarding() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", separation_type: "Resignation" });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listOffboardings({ search: search || undefined, status: statusFilter || undefined, page_size: 100 });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getEmployees({ limit: 500 }).then((data) => setEmployees(Array.isArray(data) ? data : data?.items || [])).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await createOffboarding(form);
      setShowCreate(false);
      window.location.href = `/employee-lifecycle/offboarding/${created.offboarding_id}`;
    } catch (err) {
      setError(err?.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: "var(--slate-800)" }}>Offboarding</h2>
          <p className="text-sm" style={{ color: "var(--slate-500)" }}>{total} records</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={async () => downloadBlob(await exportExitReport(), "exit_report.csv")} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border text-sm">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button type="button" onClick={() => setShowCreate(true)} className="lc-btn-primary">
            <Plus className="w-4 h-4" /> Initiate Exit
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" placeholder="Search employee…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="px-3 py-2 rounded-lg border text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {showCreate && (
        <form onSubmit={handleCreate} className="rounded-xl border p-4 space-y-3" style={{ background: "var(--bg-surface, #fff)" }}>
          <h3 className="font-medium">Initiate Offboarding</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select required className="border rounded-lg px-3 py-2 text-sm" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
              <option value="">Select employee</option>
              {employees.map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {[emp.first_name, emp.last_name].filter(Boolean).join(" ")} ({emp.employee_code})
                </option>
              ))}
            </select>
            <select className="border rounded-lg px-3 py-2 text-sm" value={form.separation_type} onChange={(e) => setForm({ ...form, separation_type: e.target.value })}>
              {SEPARATION_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <DatePickerField
              hideLabel
              compact
              value={form.last_working_day || ""}
              onChange={(date) => setForm({ ...form, last_working_day: date })}
              placeholder="Last working day"
              inputClassName="border rounded-lg px-3 py-2 text-sm w-full"
            />
            <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Reason for exit" onChange={(e) => setForm({ ...form, reason_for_exit: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm">Create</button>
            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-left px-4 py-3">Separation</th>
              <th className="text-left px-4 py-3">LWD</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Clearance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center">Loading…</td></tr>
            ) : items.map((off) => (
              <tr key={off.offboarding_id} className="border-t">
                <td className="px-4 py-3">
                  <Link to={`/employee-lifecycle/offboarding/${off.offboarding_id}`} className="text-violet-600 hover:underline font-medium">
                    {off.employee_name || "—"}
                  </Link>
                  {off.employee_code && (
                    <div className="text-xs text-slate-500 mt-0.5">{off.employee_code}</div>
                  )}
                </td>
                <td className="px-4 py-3">{off.separation_type || "—"}</td>
                <td className="px-4 py-3">{off.last_working_day || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[off.status] || "bg-slate-100"}`}>{off.status}</span>
                </td>
                <td className="px-4 py-3">
                  {(() => {
                    const { done, total } = getClearanceProgress(off.clearances);
                    return (
                      <span className={done === total && total > 0 ? "text-emerald-700 font-medium" : ""}>
                        {done}/{total}
                      </span>
                    );
                  })()}
                </td>
              </tr>
            ))}
            {!loading && !items.length && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No offboarding records</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
